/* eslint-disable no-unused-vars */
// Tasks.jsx
import React, { useEffect, useState, useRef } from 'react';
import TaskCard from './TaskCard';
import { Plus, ArrowUpDown, ChevronDown, Folder, Link } from 'lucide-react';
import AddTaskCard from './AddTaskCard';
import EmptyTaskImage from './EmptyTaskImage';
import { useAuth } from '../Contexts/AuthContext';
// eslint-disable-next-line no-unused-vars
import { v4 as uuidv4 } from 'uuid';
import TokenChecker, { WS_BASE_URL } from './TokenChecker';
import { motion, AnimatePresence } from "framer-motion";
import CreateFolder from './CreateFolder';
import { log, error as logError } from "../utils/logger";
import useFolder from "./useFolder";


const Tasks = ({
  category,
  getButtonLabel,
  isShared,
  selectedFolder,
  folders =[],
  onCreateFolder,      // now expects (name, color)
  onFolderSelect,
  folderId,          // for filtering tasks by folder
  forceFolderView,
  triggerAddTask
}) => {
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [trackCategory, setTrack] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const { authData } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sortBy, setSortBy] = useState('newly-added');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // DnD + multi-select state
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [dragging, setDragging] = useState(false);
  const [hoverFolderId, setHoverFolderId] = useState(null);

  const { attachTasksToFolder } = useFolder();


  const isFolderView = forceFolderView || !!selectedFolder || !!folderId;
  const hasFolder = Array.isArray(folders) && folders.length > 0;

  // Split-button dropdown + modal
  const [isFolderDropdownOpen, setIsFolderDropdownOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);

  const userEmail = authData.email;
  const socketRef = useRef(null);

  // Role-based permission logic
  const isOwner = category === "Personal Tasks";
  const isAssigner = category === "Tasks Delegated - Assigned";
  const isAssignee = category === "Assigned Tasks";
  const isInviter = category === "Tasks Delegated - Invitation";
  const isInvitee = category === "Invited Tasks";
  const isInviteRole = isInviter || isInvitee;

// keep names consistent: hasFolders (plural)

const shouldShowFolders = () =>
  category === "Personal Tasks" && !isFolderView && hasFolder;



  // Permissions
  const canEdit   = isOwner || isAssigner || isInviter;
  const canDelete = isOwner || isAssigner || isInviter;
  const canUpdateDueDate = isOwner || isAssigner;
  const canMarkComplete  = isOwner || isAssignee || isInviteRole;
  const canComment = isAssigner || isAssignee || isInviter || isInviteRole;

  const canChangeProgress =
    category === "Personal Tasks" ||
    category === "Assigned Tasks" ||
    category === "Invited Tasks" ||
    category === "Tasks Delegated - Invitation";

  const viewassignedandinvited =
    category === "Tasks Delegated - Assigned" ||
    category === "Tasks Delegated - Invitation";

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDelete(true);
  };
  const handleCancelDelete = () => setConfirmDelete(false);
  const handleEdit = (index) => setEditIndex(index);

  const closeAddTask = () => {
    setIsAddTaskOpen(false);
    setEditIndex(null);
  };

  const sortTasks = (arr, sortByVal) => {
    const sorted = [...arr];
    switch (sortByVal) {
      case 'newly-added':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'priority': {
        const order = { 'Immediate': 0, 'Moderate': 1, 'Lenient': 2 };
        return sorted.sort((a, b) => order[a.priority] - order[b.priority]);
      }
      case 'due-date':
        return sorted.sort((a, b) => {
          const months = {
            'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
            'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
          };
          const parseDate = (s) => {
            const parts = s.split(/[\s,]+/);
            const m = months[parts[0]];
            const d = parseInt(parts[1].replace(/\D/g, ''));
            const y = parseInt(parts[2]);
            return new Date(y, m, d);
          };
          return parseDate(a.dueDate) - parseDate(b.dueDate);
        });
      default:
        return sorted;
    }
  };

  // Selection helpers
  const isSelected = (id) => selectedTaskIds.has(id);
  const clearSelection = () => setSelectedTaskIds(new Set());
  const toggleSelect = (id, multi = false) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(multi ? prev : []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

const get_Tasks = async () => {
  // Decide endpoint
  let endpoint = null;

  if (folderId) {
    // Folder view: always use the personal API that includes folder info
    endpoint = '/api/tasks/personal/';
  } else {
    // Original category-based routing
    endpoint =
      category === 'Assigned Tasks' || category === 'Tasks Delegated - Assigned'
        ? '/get_assigned/'
        : category === 'Invited Tasks' || category === 'Tasks Delegated - Invitation'
        ? '/get_invited/'
        : category === 'Personal Tasks'
        ? '/api/tasks/personal/' // IMPORTANT: updated path
        : null;
  }

  if (!endpoint) return;

  try {
    const response = await TokenChecker(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // body is ignored by /api/tasks/personal/ (server uses request.user), but harmless
      body: JSON.stringify({ userEmail }),
    });

    const data = await response.json();
    if (!response.ok) {
      logError(data?.error || 'Failed to load tasks');
      return;
    }

    let tasks = Array.isArray(data?.tasks) ? data.tasks : [];

    // If we're viewing a folder, keep only tasks in that folder
    if (folderId) {
      tasks = tasks.filter(t => t?.folder?.id === folderId);
    } else if (category === 'Personal Tasks') {
      // ✅ KEY FIX: In main Personal Tasks view, exclude tasks that are in folders
      tasks = tasks.filter(t => !t?.folder?.id);
    } else {
      // Keep your existing owner-based filter logic for shared tasks
      tasks = tasks.filter(task => {
        if (category === 'Tasks Delegated - Assigned' || category === 'Tasks Delegated - Invitation') {
          return task.owner === userEmail;
        }
        if (category === 'Assigned Tasks' || category === 'Invited Tasks') {
          return task.owner !== userEmail;
        }
        return true;
      });
    }

    const tasksWithCategory = tasks.map(task => ({
      ...task,
      category,
      progress: task.progress,
      completed: task.completed,
      assignedUsers: task.assigned_users || [],
      invitedUsers: task.invited_users || [],
      attachmentKey: task.attachmentKey || null, // Include attachment key if available
    }));

    setTasks(tasksWithCategory);
  } catch (err) {
    logError(err);
  }
};


  useEffect(() => {
    if (!userEmail) return;
    get_Tasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, category, folderId]);

  // Clear selection when category/folder changes
  useEffect(() => {
    clearSelection();
  }, [category, folderId]);

  const deleteTask = async (id) => {
    try {
      const response = await TokenChecker(`/delete_list/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      const data = await response.json();
      if (response.ok) {
        get_Tasks();
        setTasks(prev => prev.filter((t) => t.id !== id));
        setConfirmDelete(false);
      } else {
        logError(data.error);
      }
    } catch (err) {
      logError(err);
    }
  };

  const markComplete = async (taskToMark, currentStatus) => {
    try {
      await TokenChecker(`/completed/${taskToMark}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed: !currentStatus, userEmail })
      });
      get_Tasks();
    } catch (err) {
      logError(err);
    }
  };

  const updateDueDate = async (task, newDueDate) => {
  try {
    const payload = {
      id: task.id,
      owner: userEmail,
      title: task.title,
      description: task.description,
      priority: task.priority,
      dueDate: newDueDate,
      // ✅ keep folder association if present
      folder_id: folderId ?? task.folder?.id ?? null,
    };

    const response = await TokenChecker('/create_list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      // Optional optimistic update to avoid flicker:
      setTasks(prev =>
        prev.map(t => (t.id === task.id ? { ...t, dueDate: newDueDate, folder: task.folder ?? null } : t))
      );
      get_Tasks();
    } else {
      const data = await response.json();
      logError(data.error);
    }
  } catch (err) {
    logError(err);
  }
};


  const shouldShowAddTaskButton =
    category === 'Personal Tasks' ||
    category === 'Tasks Delegated - Assigned' ||
    category === 'Tasks Delegated - Invitation';

  const onAddTask = () => {
    setTrack(category);
    setIsAddTaskOpen(true);
  };

  // Open Add Task only on new trigger increments (debounced across mounts)
  const lastTriggerRef = useRef(triggerAddTask);
  useEffect(() => {
    if (triggerAddTask > lastTriggerRef.current) {
      if (shouldShowAddTaskButton) {
        onAddTask();
      }
      lastTriggerRef.current = triggerAddTask;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAddTask]);

  // Split button: "Create Folder"
  const handleCreateFolderClick = () => {
    setIsFolderDropdownOpen(false);
    setIsCreateFolderOpen(true);
  };

  useEffect(() => {
    if (isAddTaskOpen && category !== trackCategory) setIsAddTaskOpen(false);
  }, [category, trackCategory, isAddTaskOpen]);

  const getIsCompletedView = (tab) => {
    if (tab === 'due-now') {
      return !(
        category === 'Personal Tasks' ||
        category === 'Tasks Delegated - Assigned' ||
        category === 'Tasks Delegated - Invitation'
      );
    }
    return true;
  };

  const sortOptions = [
    { value: 'newly-added', label: 'Newly Added' },
    { value: 'priority',    label: 'Priority' },
    { value: 'due-date',    label: 'Due Date' }
  ];

  const handleSortChange = (value) => {
    setSortBy(value);
    setIsDropdownOpen(false);
  };

  // Filter by selected folder (when we wire folderId in tasks later)
  const getFilteredTasks = (allTasks) => {
    if (!selectedFolder) return allTasks;
    return allTasks.filter(task => task.folderId === selectedFolder.id);
  };

  // Drag handlers for folder pills
 const handleFolderDragOver = (e, fid) => {
  e.preventDefault();
  e.stopPropagation();
  // Hint a move operation for better UX and Safari compatibility
  try { if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; } catch (_) {}
  setHoverFolderId(fid);
 };

 const handleFolderDragLeave = (e, fid) => {
  // Remove preventDefault() - it's not needed for dragLeave
  e.stopPropagation();
  // Only clear if we're actually leaving this folder
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX;
  const y = e.clientY;
  
  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    setHoverFolderId((cur) => (cur === fid ? null : cur));
  }
};
  const handleFolderDrop = async (e, folder) => {
  e.preventDefault();
  e.stopPropagation();
  setHoverFolderId(null);
  setDragging(false);
  
  try {
    // Improved data retrieval
    let payload;
    try {
      payload = e.dataTransfer.getData('application/json');
      if (!payload) {
        payload = e.dataTransfer.getData('text/plain');
      }
    } catch (error) {
      console.error('Failed to get drag data:', error?.code);
      return;
    }

    const parsed = payload ? JSON.parse(payload) : null;
    if (!parsed) {
      console.warn('No drag data found');
      return;
    }

    let ids = [];
    if (parsed && Array.isArray(parsed.task_ids)) {
      ids = parsed.task_ids;
    } else if (parsed && parsed.task_id) {
      ids = [parsed.task_id];
    }

    if (!ids.length) {
      console.warn('No task IDs found in drag data');
      return;
    }

    console.log('Dropping tasks:', ids, 'into folder:', folder.name);

    const res = await attachTasksToFolder(folder.id, ids);

    // Optimistic UI update
    setTasks((prev) => 
      prev.map((t) => 
        ids.includes(t.id) 
          ? { ...t, folder: { id: folder.id, name: folder.name, color: folder.color } }
          : t
      ).filter((t) => {
        // If we're in personal tasks view, remove tasks that now have folders
        return !(category === "Personal Tasks" && !folderId && ids.includes(t.id));
      })
    );

    clearSelection();
    
    // Refresh to ensure consistency
    setTimeout(() => {
      get_Tasks();
    }, 100);

  } catch (err) {
    console.error('Drop operation failed:', err?.response?.status, err?.code);
    logError(err);
  }
};




// 2. Fix the task drag implementation
const TaskDragWrapper = ({ task, children, isSelected, onSelect }) => {
  const handleDragStart = (e) => {
    console.log('Drag started for task:', task.id);
    
    // Determine which tasks to drag
    const ids = selectedTaskIds.size && isSelected(task.id)
      ? Array.from(selectedTaskIds)
      : [task.id];

    // Set drag data with better error handling
    const dragData = JSON.stringify({ task_ids: ids });
    
    try {
      e.dataTransfer.setData('application/json', dragData);
      e.dataTransfer.setData('text/plain', dragData); // Fallback
    } catch (error) {
      console.error('Failed to set drag data:', error?.code);
      e.dataTransfer.setData('text/plain', dragData);
    }

    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';
    
    setDragging(true);
    console.log('Dragging task IDs:', ids);
  };

  const handleDragEnd = (e) => {
    console.log('Drag ended');
    setDragging(false);
  };

  const handleClick = (e) => {
    // Only handle selection on modifier key clicks
    // This prevents interference with drag operations
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(task.id, true);
    }
  };

  const handleMouseDown = (e) => {
    // Allow drag to start properly
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
      // Clear selection if clicking on unselected task without modifier
      if (!isSelected(task.id) && selectedTaskIds.size > 0) {
        clearSelection();
      }
    }
  };

  return (
    <div
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      className={`rounded-lg transition-all duration-200 cursor-move ${
        isSelected(task.id) ? 'ring-2 ring-[#4299E1] ring-offset-1' : ''
      } ${dragging ? 'opacity-50' : 'opacity-100'}`}
      style={{
        userSelect: 'none', // Prevent text selection during drag
      }}
    >
      {children}
    </div>
  );
};


const FolderPill = ({ folder, onSelect, hoverFolderId, onDragOver, onDragLeave, onDrop }) => {
  const isHovered = hoverFolderId === folder.id;
  
  return (
    <div
      onClick={() => onSelect(folder)}
      onDragOver={(e) => onDragOver(e, folder.id)}
      onDragLeave={(e) => onDragLeave(e, folder.id)}
      onDrop={(e) => onDrop(e, folder)}
      className={`
        flex-shrink-0 bg-white rounded-lg p-4 border-l-4 shadow cursor-pointer 
        transition-all duration-200 min-w-[200px]
        ${isHovered 
          ? 'ring-2 ring-offset-2 ring-[#4299E1] scale-105 bg-blue-50' 
          : 'hover:shadow-md hover:scale-105'
        }
      `}
      style={{ 
        borderLeftColor: folder.color,
        // Visual feedback for valid drop target
        ...(isHovered && {
          boxShadow: `0 0 0 2px ${folder.color}40`,
        })
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-[#2D3748] text-sm">{folder.name}</h4>
          <p className="text-[#718096] text-xs mt-1">
            {folder.task_counts?.total || 0} tasks
          </p>
        </div>
        {folder.baseFileUrl && (
          <Link className="w-4 h-4" style={{ color: folder.color }} />
        )}
      </div>
      
      {/* Drop zone indicator */}
      {isHovered && (
        <div className="absolute inset-0 border-2 border-dashed border-[#4299E1] rounded-lg pointer-events-none" />
      )}
    </div>
  );
};




  // Task WebSocket (live updates)
  useEffect(() => {
    if (!authData?.accessToken) return;
    // Avoid tokens in URL; pass as WebSocket subprotocol
    socketRef.current = new WebSocket(`${WS_BASE_URL}/ws/tasks/`, ['jwt', authData.accessToken]);

    socketRef.current.onopen = () => log("📡 Task WebSocket connected");
    socketRef.current.onclose = (e) => log("❌ Task WebSocket disconnected", e.code, e.reason);
    socketRef.current.onerror = (err) => logError("🚨 WebSocket error", err);

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const { type, task_id } = data;

      if (type === "task_delete") {
        setTasks(prev => prev.filter(t => t.id !== task_id));
      } else if (type === "task_complete") {
        setTasks(prev => prev.map(t => (t.id === task_id ? { ...t, completed: data.completed } : t)));
      } else if (type === "progress_update") {
        setTasks(prev => prev.map(t => (t.id === task_id ? { ...t, progress: data.progress } : t)));
      } else if (type === "task_update") {
        setTasks(prev =>
          prev.map(t =>
            t.id === task_id
              ? {
                  ...t,
                  title:       data.title       ?? t.title,
                  description: data.description ?? t.description,
                  dueDate:     data.dueDate     ?? t.dueDate,
                  priority:    data.priority    ?? t.priority,
                  progress:    data.progress    ?? t.progress
                }
              : t
          )
        );
      }
    };

    return () => socketRef.current?.close();
  }, [authData]);




  return (
    <>
      <Tabs defaultValue="due-now">
        <TabsList>
          <TabsTrigger value="due-now">Due Now</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

    

        <TabsContent value="due-now">
          <div className="flex justify-between items-center my-2">
            <h3 className="text-xl font-semibold text-[#2D3748]">
              {selectedFolder ? selectedFolder.name : 'Due Now'}
            </h3>

            <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              {tasks.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#4A5568] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                  >
                    <ArrowUpDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    {sortOptions.find(option => option.value === sortBy)?.label}
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                            sortBy === option.value ? 'bg-[#EBF8FF] text-[#4299E1] font-medium' : 'text-[#4A5568]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Split Add Task / ▾ */}
              {shouldShowAddTaskButton && (
                <div className="relative inline-flex">
                  <div className="relative flex overflow-visible rounded-md shadow-md z-20">
                    {/* PRIMARY ACTION (fixed: now calls onAddTask) */}
                    <Button
                      className={`flex items-center ${(folderId || !isOwner)? 'rounded':'rounded-r-none border-r border-[#3182CE]/40'}`}
                      onClick={onAddTask}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {getButtonLabel()}
                    </Button>

                    {/* Chevron + short subtle divider stub */}
                    {(!folderId && isOwner) && (
                      <button
                      onClick={() => setIsFolderDropdownOpen(v => !v)}
                      className="relative bg-[#4299E1] hover:bg-[#3182CE] text-white px-2 py-2 rounded-r-md"
                    >
                      <span className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 -ml-[1px] h-4 w-px bg-[#d3d7df]/50" />
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    )}
                    
                  </div>

                  {/* Connected dropdown */}
                  <AnimatePresence>
                    {isFolderDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -6, height: 0 }}
                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute right-0 left-0 top-full z-10 w-[180px] -mt-1"
                      >
                        <div className="overflow-hidden w-[165px] rounded-b-lg rounded-t-none border border-gray-200 shadow-xl">
                          <div className="h-1 bg-[#4299E1]" />
                          <div className="bg-[#4299E1]">
                            <button
                              onClick={handleCreateFolderClick}
                              className="w-full px-4 py-2 text-sm text-center text-white hover:bg-[#3182CE]"
                            >
                              <Folder className="inline mr-2 h-4 w-4" />
                              Create Folder
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Click-away for dropdown/modal */}
          {(isDropdownOpen || isFolderDropdownOpen) && (
            <div
              className="fixed inset-0 z-0"
              onClick={() => {
                setIsDropdownOpen(false);
                setIsFolderDropdownOpen(false);
              }}
            />
          )}

              {/* Folder pills */}
        {shouldShowFolders() && (
          <div className="mb-4">
            <div className="flex items-center gap-4 overflow-x-auto overflow-y-hidden pl-2 scrollbar-hide">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  onClick={() => onFolderSelect(folder)}
                  onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                  onDragEnter={(e) => handleFolderDragOver(e, folder.id)}
                  onDragLeave={(e) => handleFolderDragLeave(e, folder.id)}
                  onDrop={(e) => handleFolderDrop(e, folder)}
                  className={`flex-shrink-0 bg-white rounded-lg p-4 border-l-4 shadow cursor-pointer transition-all duration-200 min-w-[200px] ${
                    hoverFolderId === folder.id ? 'ring-2 ring-offset-2 ring-[#4299E1] scale-105' : 'hover:shadow-md hover:scale-105'
                  }`}
                  style={{ borderLeftColor: folder.color }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[#2D3748] text-sm">{folder.name}</h4>
                      {(()=>{
                        const n = folder.task_count ?? folder.tasks_count ?? 0;
                        return <p className="text-[#718096] text-xs mt-1"> 
                        {n} {n === 1 ? 'task' : 'tasks'}
                        </p>;
                      })}
                    </div>
                    {folder.attachmentUrl && (
                       <Link className="w-4 h-4" style={{ color: folder.color }} />
                    )}
                 
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Create Folder Modal (fixed: pass color up) */}
          {isCreateFolderOpen && (
            <CreateFolder
              isOpen={isCreateFolderOpen}
              onClose={() => setIsCreateFolderOpen(false)}
              onCreateFolder={(folderName, color, baseFile, baseFile_label) => {
                onCreateFolder(folderName, color, baseFile, baseFile_label);
                setIsCreateFolderOpen(false);
              }}
            />
          )}

          {isAddTaskOpen && !editIndex && (
            <AddTaskCard
              onAddTask={async () => {
                          await get_Tasks();      
                          setIsAddTaskOpen(false);    
                        }}
              onCancel={() => setIsAddTaskOpen(false)}
              isShared={isShared}
              buttonLabel={getButtonLabel()}
              category={category}
              folderId={folderId}
              forceFolderView={forceFolderView}
            />
          )}

          {!isAddTaskOpen  && !folderId &&
          getFilteredTasks(tasks.filter((t) => t.category === category && !t.completed)).length === 0 ? (
            <EmptyTaskImage onAddTask={onAddTask} isCompletedView={getIsCompletedView('due-now')} />
          ) : (
            sortTasks(
              getFilteredTasks(tasks.filter((t) => t.category === category && !t.completed)),
              sortBy
            ).map((task) =>
              editIndex === task.id ? (
                <AddTaskCard
                  key={task.id}
                  onAddTask={async () => {
                      await get_Tasks();          
                      setIsAddTaskOpen(false);   
                      setEditIndex(null); 
                    }}
                  onCancel={closeAddTask}
                  isShared={isShared}
                  buttonLabel="Save Task"
                  category={category}
                  initialData={task}
                  task_id={editIndex}
                  forceFolderView={forceFolderView}
                  folderId={folderId}
                />
              ) : (
                <div
                  key={task.id}
                  draggable
                  onDragStart={(e) => {
                    // If dragging one that's not selected, drag only it; otherwise drag all selected
                    const ids = selectedTaskIds.size && isSelected(task.id)
                      ? Array.from(selectedTaskIds)
                      : [task.id];
                    const dragData = JSON.stringify({ task_ids: ids });
                    try {
                      e.dataTransfer.setData('application/json', dragData);
                      e.dataTransfer.setData('text/plain', dragData); // Safari fallback
                    } catch (_err) {
                      e.dataTransfer.setData('text/plain', dragData);
                    }
                    try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {}
                    setDragging(true);
                  }}
                  onDragEnd={() => setDragging(false)}
                  onClick={(e) => {
                    // Only toggle selection when using modifier keys to avoid interfering with card actions
                    const multi = e.metaKey || e.ctrlKey || e.shiftKey;
                    if (multi) toggleSelect(task.id, true);
                  }}
                  className={`rounded-lg transition-shadow ${isSelected(task.id) ? 'ring-2 ring-[#4299E1]' : ''}`}
                >
                  <TaskCard
                    id={task.id}
                    title={task.title}
                    description={task.description}
                    dueDate={task.dueDate}
                    priority={task.priority}
                    progress={task.progress}
                    owner={
                      category === "Tasks Delegated - Assigned" || category === "Tasks Delegated - Invitation"
                        ? "You"
                        : task.firstName
                    }
                    isCompleted={false}
                    markComplete={() => markComplete(task.id, task.completed)}
                    onDelete={() => handleDelete(task.id)}
                    category={canUpdateDueDate}
                    allowEdit={canEdit}
                    allowDelete={canDelete}
                    allowComplete={canMarkComplete}
                    allowProgress={canChangeProgress}
                    handleEdit={() => handleEdit(task.id)}
                    onDueDateChange={(newDate) => updateDueDate(task, newDate)}
                    viewusers={viewassignedandinvited}
                    assignedUsers={task.assignedUsers}
                    invitedUsers={task.invitedUsers}
                    isTrusted={task.is_trusted}
                    isAccepted={task.is_accepted}
                    canComment={canComment}
                    attachmentKey={task.attachmentKey}
                  />
                </div>
              )
            )
          )}

          {confirmDelete && (
            <div className="fixed inset-0 h-full backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white border-t-4 border-[#4299E1] rounded-lg p-6 max-w-md w-full shadow-lg">
                <h2 className="text-xl font-semibold mb-4">Delete Task</h2>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this task? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteTask(deleteId)}
                    className="px-4 py-2 text-white bg-[#FF3B30] rounded-lg hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

     <TabsContent value="completed">
  <div className="text-xl font-semibold text-[#2D3748] my-4 flex justify-start">
    Completed
  </div>
  {getFilteredTasks(
    tasks
      .filter((t) => t.category === category && t.completed)
      // sort by latest first (descending)
      .sort((a, b) => new Date(b.completedAt || b.updatedAt || b.createdAt) - new Date(a.completedAt || a.updatedAt || a.createdAt))
  ).length === 0 ? (
    <EmptyTaskImage isCompletedView={getIsCompletedView("completed")} />
  ) : (
    getFilteredTasks(
      tasks
        .filter((t) => t.category === category && t.completed)
        .sort((a, b) => new Date(b.completedAt ) - new Date(a.completedAt ))
    ).map((task) => (
      <TaskCard
        key={task.id}
        id={task.id}
        title={task.title}
        description={task.description}
        dueDate={task.dueDate}
        priority={task.priority}
        owner={
          category === "Tasks Delegated - Assigned" || category === "Tasks Delegated - Invitation"
            ? "You"
            : task.firstName
        }
        isCompleted={true}
        markComplete={() => markComplete(task.id, true)}
        onDelete={() => deleteTask(task.id)}
        category={canUpdateDueDate}
        allowEdit={canEdit}
        allowDelete={canDelete}
        allowComplete={canMarkComplete}
        allowProgress={canChangeProgress}
        viewusers={viewassignedandinvited}
        assignedUsers={task.assignedUsers}
        invitedUsers={task.invitedUsers}
        attachmentKey={task.attachment_key}
      />
    ))
  )}
</TabsContent>

      </Tabs>
    </>
  );
};

export default Tasks;

const Button = ({ variant = 'default', className = '', children, ...props }) => {
  const baseStyles = 'px-4 py-2 rounded-md font-medium';
  const variantStyles = {
    default: 'bg-[#4299E1] text-white hover:bg-[#3182CE]',
    outline: 'border border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]',
    ghost:   'text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF]',
  };

  return (
    <button className={`${baseStyles} ${variantStyles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Tabs = ({ children, defaultValue }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  return (
    <div className="space-y-4">
      {React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child, i) =>
          React.cloneElement(child, { key: child.key ?? i, activeTab, setActiveTab })
        )}
    </div>
  );
};

const TabsList = ({ children, activeTab, setActiveTab }) => (
  <div className="flex space-x-1 border-b border-gray-200">
    {React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child, i) =>
        React.cloneElement(child, {
          key: child.key ?? i,
          isActive: child.props.value === activeTab,
          onClick: () => setActiveTab(child.props.value),
        })
      )}
  </div>
);


const TabsTrigger = ({ children, value, isActive, onClick }) => (
  <button
    className={`px-4 py-2 text-sm font-medium text-[#4A5568] ${
      isActive ? ' bg-white rounded-lg text-[#4299E1] border-b-2 border-[#4299E1]' : ''
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

const TabsContent = ({ children, value, activeTab }) =>
  activeTab === value ? children : null;
